<script setup lang="ts">
import { BButton, BSpinner } from 'bootstrap-vue-next';

// The status span is a sibling of the button, not a child, so the live region is not part of the
// button's accessible name. That makes this component multi-root: attributes only reach the button
// because of inheritAttrs, and v-show, Transition and template refs do not work on LoadingButton.
defineOptions({ inheritAttrs: false });

// Declaring `loading` and `loadingText` as props keeps them out of $attrs. BButton has props with
// the same names, and forwarding `loading` would bring back its label swap without blocking clicks.
const props = withDefaults(defineProps<{
  loading?: boolean;
  loadingText?: string;
}>(), {
  loadingText: 'Loading...',
});

// Bound with @click.capture so the click is cancelled before BButton emits its own click and before
// the browser runs the default action, which would submit the form of a submit button.
const blockWhileLoading = (event: MouseEvent) => {
  if (!props.loading) {
    return;
  }

  event.preventDefault();
  event.stopPropagation();
};
</script>

<template>
  <BButton
    v-bind="$attrs"
    class="loading-button"
    :class="{ 'loading-button-busy': loading }"
    :aria-busy="loading ? 'true' : null"
    :aria-disabled="loading ? 'true' : null"
    @click.capture="blockWhileLoading"
  >
    <span class="loading-button-label"><slot /></span>
    <BSpinner
      v-if="loading"
      small
      class="loading-button-spinner"
    />
  </BButton>
  <span
    class="visually-hidden"
    role="status"
  >{{ loading ? loadingText : '' }}</span>
</template>
